import { jwtDecode } from "jwt-decode";
import { client } from "lib/api";
import { decodeJwtUser, JwtUser } from "utils";

const EXPIRATION_TIME_BUFFER = 10 * 60 * 1000; // 10 minutes

function isTokenAboutToExpire(accessToken: string): boolean {
  const decoded = jwtDecode(accessToken);
  const { exp } = decoded;
  if (!exp) return true;
  const expirationTime = exp * 1000;
  const now = Date.now();
  return expirationTime - now < EXPIRATION_TIME_BUFFER;
}

async function refreshToken() {
  const r = await client.POST("/api/auth/refresh");
  if (!r.error) {
    return r.data.token;
  } else {
    console.error(r.error);
  }
  return null;
}

export interface ApiAuth {
  getToken: () => Promise<string>;
  getSyncToken: () => string;
  user: JwtUser;
}

export type OnApiAuthRefreshFailed = () => void;

export function createAuth(
  initialAccessToken: string,
  onRefreshFailed: OnApiAuthRefreshFailed
): ApiAuth {
  let accessToken = initialAccessToken;
  let refreshPromise: Promise<string | null> | null = null;
  const user = decodeJwtUser(initialAccessToken);

  async function getToken() {
    if (isTokenAboutToExpire(accessToken)) {
      // If there's already a refresh in progress, wait for it
      if (refreshPromise) {
        const newToken = await refreshPromise;
        if (newToken) {
          accessToken = newToken;
        } else {
          onRefreshFailed();
        }
      } else {
        // Start a new refresh and store the promise
        refreshPromise = refreshToken();
        const newToken = await refreshPromise;
        refreshPromise = null; // Clear the promise after completion

        if (newToken) {
          accessToken = newToken;
        } else {
          onRefreshFailed();
        }
      }
    }
    return accessToken;
  }

  return {
    getToken,
    getSyncToken: () => {
      return accessToken;
    },
    user,
  };
}
